import { useState, useRef, useEffect, useCallback } from 'react'
import SEO from '../components/SEO/SEO'
import Layout from '../layout/Layout'
import classes from '../styles/section.module.css'

const mobileAppText = [
  {
    title: 'Customer Service',
    description:
      "Chat bot? Check. Text message? Check. Email? Check. A real human on the phone? Double check. However you need help, we're here to serve you.",
    image:
      'https://content.evryhealth.com/MarketingImages/mobile%20app%20slide2.png',
    altText: 'Evry Care App customer support screen',
  },
  {
    title: 'Telemedicine',
    description:
      'The help you need is now always in your pocket. $0 unlimited virtual visits.',
    image:
      'https://content.evryhealth.com/MarketingImages/mobile%20app%20slide4.png',
    altText: 'Evry Care App telemedicine screen',
  },
  {
    title: 'Cash Rewards',
    description:
      'No more confusing point systems. Earn cold hard cash loaded onto your Evry Spending Card that can be used at select retailers nationwide and online.',
    image:
      'https://content.evryhealth.com/MarketingImages/mobile%20app%20slide3.png',
    altText: 'Evry Care App cash rewards screen',
  },
  {
    title: 'Personalized Educational Resources',
    description:
      "No matter what you're dealing with, you'll always learn something useful. Interested in trying a Keto diet? Need to know the side effects of Tylenol? What's included in a Chem-20 blood panel? We'll get you started.",
    image:
      'https://content.evryhealth.com/MarketingImages/mobile%20app%20slide5.png',
    altText: 'Evry Care App articles and resources screen',
  },
  {
    title: 'Community',
    description:
      'Keep up to date with the latest health news and the always evolving features of your new plan.',
    image:
      'https://content.evryhealth.com/MarketingImages/mobile%20app%20slide6.png',
    altText: 'Evry Care Appl community screen',
  },
  {
    title: 'Coverage',
    description:
      'Understand your benefits, out of pocket maximum, claims history, and prescriptions all at a glance. No more confusing EOBs or policy documents written for lawyers.',
    image:
      'https://content.evryhealth.com/MarketingImages/mobile%20app%20slide1.png',
    altText: 'Evry Care App coverage screen',
  },
  {
    title: 'Member Tools',
    description:
      'Need a new ID card? Want to view some kind of membership document? Interested in looking up a provider? These and more are all one click away.',
    image: 'https://content.evryhealth.com/MarketingImages/public%20(11).webp',
    altText: 'Evry Care App member tools screen',
  },
]

const fadeInLeft = [
  { opacity: 0, transform: 'translateX(-100px)' },
  { opacity: 1, transform: 'translateX(0)' },
]
const fadeOutLeft = [
  { opacity: 1, transform: 'translateX(0)' },
  { opacity: 0, transform: 'translateX(-100px)' },
]
const fadeInRight = [
  { opacity: 0, transform: 'translateX(100px)' },
  { opacity: 1, transform: 'translateX(0)' },
]
const fadeOutRight = [
  { opacity: 1, transform: 'translateX(0)' },
  { opacity: 0, transform: 'translateX(100px)' },
]

const MobileApp = () => {
  const [carouselIndex, setCarouselIndex] = useState(0)
  const { image, altText, description, title } = mobileAppText[carouselIndex]

  const textRef = useRef(null)
  const imageRef = useRef(null)

  const slideLen = mobileAppText.length - 1
  const nextSlide = useCallback(async () => {
    imageRef.current?.animate(fadeOutLeft, { duration: 250 })
    await textRef.current?.animate(fadeOutLeft, { duration: 250 }).finished

    setCarouselIndex(oldIndex => (oldIndex + 1) % slideLen)

    imageRef.current?.animate(fadeInRight, { duration: 250 })
    textRef.current?.animate(fadeInRight, { duration: 250 })
  }, [setCarouselIndex, imageRef, textRef, slideLen])

  const prevSlide = useCallback(async () => {
    imageRef.current?.animate(fadeOutRight, { duration: 250 })
    await textRef.current?.animate(fadeOutRight, { duration: 250 }).finished

    setCarouselIndex(oldIndex => (oldIndex - 1 + slideLen) % slideLen)

    imageRef.current?.animate(fadeInLeft, { duration: 250 })
    textRef.current?.animate(fadeInLeft, { duration: 250 })
  }, [setCarouselIndex, imageRef, textRef, slideLen])

  //reset animation on slide change
  const reset = useCallback(() => {
    imageRef.current?.animate(fadeInRight, { duration: 0 })
    textRef.current?.animate(fadeInRight, { duration: 0 })
  }, [imageRef, textRef])

  useEffect(() => {
    const intId = setInterval(nextSlide, 5000)
    return () => clearInterval(intId)
  }, [nextSlide])

  return (
    <>
      <SEO />
      <Layout>
        <div>
          <div className="relative">
            <div className="absolute inset-x-0 bottom-0 bg-gray-100 h-1/2" />
            <div className="mx-auto">
              <div className="relative">
                <div className="absolute inset-0">
                  <img
                    className="object-cover w-full h-full"
                    src="work-colleagues-hands.png"
                    alt="Colleagues working together"
                  />
                </div>
                <div className="relative px-4 py-16 sm:px-6 sm:py-24 lg:py-32 lg:px-32">
                  <h1 className="text-4xl tracking-tight font-extralight sm:text-5xl lg:text-6xl">
                    <span className="block text-blue-dark">
                      Download the Evry mobile app.
                    </span>
                    <span className="block text-globeGreen">
                      All the features you expect.
                    </span>
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        <section>
          <div className="flex flex-col items-center justify-center max-w-screen-xl gap-20 px-4 py-12 mx-auto sm:px-6 md:px-8">
            <video
              controls
              className="w-[calc(100vw*0.6)] h-[calc(100vw*0.34)] rounded-lg shadow-lg max-sm:w-full max-sm:h-[calc(100vw*0.5)]"
            >
              <source
                src="https://content.evryhealth.com/MarketingImages/EvryHealth2024_GPS_2Badges.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </section>
        <section className="flex items-center justify-center p-10 mb-32 bg-white">
          <div>
            <h2 className="text-3xl tracking-tight text-center text-blue-dark md:text-4xl">
              Available for download on{' '}
              <span className="inline lg:block">iOS and Android.</span>
            </h2>
            <div className="flex gap-3 mt-8">
              <a
                href="https://play.google.com/store/apps/details?id=com.evryhealth.app"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://content.evryhealth.com/MarketingImages/playstoreDownload.webp"
                  className="w-64"
                  alt="Play store icon"
                />
              </a>
              <a
                href="https://apps.apple.com/us/app/evry-health/id6443609235"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://content.evryhealth.com/MarketingImages/appstoreDownload.webp"
                  className="w-64 "
                  alt="App store icon"
                />
              </a>
            </div>
          </div>
        </section>
        <section>
          <div className="flex items-center max-w-screen-xl px-4 py-12 mx-auto sm:px-6 md:px-8">
            <div
              style={{ marginTop: '-250px' }}
              onClick={prevSlide}
              className={classes.prevArrow}
            />
            <div className="md:grid md:grid-cols-2 lg:gap-8 lg:items-center">
              <div className="h-full md:col-span-1">
                <div className="md:pt-24 md:pl-32 h-60 md:h-72" ref={textRef}>
                  <h2 className="text-3xl tracking-tight text-blue-dark md:text-4xl">
                    {title}
                  </h2>
                  <p className="max-w-lg mt-6 text-lg text-grey">
                    {description}
                  </p>
                </div>
              </div>
              <div className="md:col-span-1">
                <div
                  className="flex items-center justify-center h-full"
                  ref={imageRef}
                >
                  <img
                    className={`${classes.transformPhone} z-1`}
                    src={image}
                    alt={altText}
                    style={{
                      width: 'calc(100vw * 0.2)',
                      height: 'calc(100vw * 0.4)',
                    }}
                  />
                </div>
              </div>
            </div>
            <div
              style={{ marginTop: '-250px' }}
              onClick={nextSlide}
              className={classes.nextArrow}
            />
          </div>
        </section>
        <main></main>
      </Layout>
    </>
  )
}

export default MobileApp
